import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // ✅ UPDATED: Use dynamic theme colors instead of hardcoded
        default: [
          "bg-gradient-to-r from-theme-primary to-theme-secondary text-white",
          "hover:from-theme-primary-hover hover:to-theme-secondary-hover",
          "focus:ring-4 focus:ring-theme-primary/30",
          "shadow-lg shadow-theme-primary/25 hover:shadow-theme-primary/40"
        ],
        destructive: [
          "bg-gradient-to-r from-red-500 to-red-600 text-white",
          "hover:from-red-600 hover:to-red-700",
          "focus:ring-4 focus:ring-red-300 dark:focus:ring-red-800",
          "shadow-lg shadow-red-500/25 hover:shadow-red-500/40"
        ],
        outline: [
          "border-2 border-theme-primary bg-transparent",
          "text-theme-primary hover:text-white",
          "hover:bg-gradient-to-r hover:from-theme-primary hover:to-theme-secondary",
          "focus:ring-4 focus:ring-theme-primary/30"
        ],
        secondary: [
          "bg-gradient-to-r from-theme-secondary to-theme-accent text-white",
          "hover:from-theme-secondary-hover hover:to-theme-accent",
          "focus:ring-4 focus:ring-theme-secondary/30",
          "shadow-lg shadow-theme-secondary/25 hover:shadow-theme-secondary/40"
        ],
        ghost: [
          "text-theme-primary hover:text-white",
          "hover:bg-gradient-to-r hover:from-theme-primary/10 hover:to-theme-secondary/10",
          "focus:ring-4 focus:ring-theme-primary/30"
        ],
        link: [
          "text-theme-primary",
          "underline-offset-4 hover:underline",
          "hover:text-theme-primary-hover"
        ],
        // Keep specialty variants but make them static
        ocean: [
          "bg-gradient-to-r from-blue-600 via-theme-primary to-blue-800 text-white",
          "hover:from-blue-700 hover:via-purple-700 hover:to-blue-900",
          "focus:ring-4 focus:ring-blue-300",
          "shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40"
        ],
        sunset: [
          "bg-gradient-to-r from-orange-400 via-pink-500 to-red-500 text-white",
          "hover:from-orange-500 hover:via-theme-secondary hover:to-red-600",
          "focus:ring-4 focus:ring-orange-300",
          "shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40"
        ],
        emerald: [
          "bg-gradient-to-r from-emerald-400 to-cyan-400 text-white",
          "hover:from-emerald-500 hover:to-cyan-500",
          "focus:ring-4 focus:ring-emerald-300",
          "shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40"
        ],
        gold: [
          "bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 text-white",
          "hover:from-yellow-500 hover:via-orange-600 hover:to-red-600",
          "focus:ring-4 focus:ring-yellow-300",
          "shadow-lg shadow-yellow-500/25 hover:shadow-yellow-500/40"
        ],
        neon: [
          "bg-gradient-to-r from-purple-400 via-pink-500 to-red-500 text-white",
          "hover:from-theme-primary hover:via-theme-secondary hover:to-red-600",
          "focus:ring-4 focus:ring-theme-primary/30",
          "shadow-lg shadow-theme-primary/30 hover:shadow-theme-primary/50",
          "animate-pulse hover:animate-none"
        ],
        cosmic: [
          "bg-gradient-to-br from-theme-primary to-blue-500 text-white",
          "hover:bg-gradient-to-bl hover:from-theme-primary-hover hover:to-blue-600",
          "focus:ring-4 focus:ring-blue-300",
          "shadow-lg shadow-theme-primary/25 hover:shadow-blue-500/40"
        ],
        // ✅ NEW: Theme-aware variant that uses current theme
        theme: [
          "bg-gradient-to-r from-theme-primary to-theme-secondary text-white",
          "hover:from-theme-primary-hover hover:to-theme-secondary-hover",
          "focus:ring-4 focus:ring-theme-primary/30",
          "shadow-lg shadow-theme-primary/25 hover:shadow-theme-primary/40"
        ],
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3 text-sm",
        lg: "h-11 rounded-xl px-8 text-base font-semibold",
        xl: "h-14 rounded-2xl px-10 text-lg font-bold",
        icon: "h-10 w-10",
      },
      glow: {
        true: "hover:scale-105 transform transition-transform duration-300",
        false: ""
      }
    },
    compoundVariants: [
      {
        variant: "default",
        glow: true,
        class: "hover:shadow-2xl hover:shadow-theme-primary/50"
      },
      {
        variant: "theme",
        glow: true,
        class: "hover:shadow-2xl hover:shadow-theme-primary/50"
      },
      {
        variant: "ocean",
        glow: true,
        class: "hover:shadow-2xl hover:shadow-blue-500/50"
      },
      {
        variant: "sunset",
        glow: true,
        class: "hover:shadow-2xl hover:shadow-orange-500/50"
      },
      {
        variant: "neon",
        size: "lg",
        class: "shadow-2xl shadow-theme-primary/40"
      }
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
      glow: false
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, glow, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, glow, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
